import { useState, useEffect, useCallback } from "react";
import { Package, Plus, BookUser, LogOut, Truck, Copy, Settings, Lock, Loader2, Warehouse } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import CustomerShipments from "./CustomerShipments";
import NewShipmentForm from "./NewShipmentForm";
import AddressBook from "./AddressBook";
import CustomerWarehouse from "./CustomerWarehouse";
import PWAInstallButton from "../PWAInstallButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_TABS = [
  { id: "shipments", label: "Spedizioni", icon: Package },
  { id: "new-shipment", label: "Nuova Spedizione", icon: Plus },
  { id: "pickups", label: "Ritiri", icon: Truck },
  { id: "new-pickup", label: "Nuovo Ritiro", icon: Plus },
  { id: "addresses", label: "Rubrica", icon: BookUser },
  { id: "settings", label: "Impostazioni", icon: Settings },
];

export default function CustomerDashboard({ user, token, onLogout }) {
  const [activeTab, setActiveTab] = useState("shipments");
  const [shipments, setShipments] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const TABS = user?.warehouse_enabled
    ? [...BASE_TABS.slice(0, 4), { id: "warehouse", label: "Magazzino", icon: Warehouse }, ...BASE_TABS.slice(4)]
    : BASE_TABS;

  const fetchShipments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/shipments`, { headers: { Authorization: `Bearer ${token}` } });
      setShipments(res.data);
    } catch (e) { /* silent: dashboard shows empty state on fetch error */ }
  }, [token]);

  const fetchPickups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pickups`, { headers: { Authorization: `Bearer ${token}` } });
      setPickups(res.data);
    } catch (e) { /* silent: dashboard shows empty state on fetch error */ }
  }, [token]);

  const fetchAddresses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/addresses`, { headers: { Authorization: `Bearer ${token}` } });
      setAddresses(res.data);
    } catch (e) { /* silent: address book optional, fallback to empty list */ }
  }, [token]);

  useEffect(() => {
    fetchShipments();
    fetchPickups();
    fetchAddresses();
  }, [fetchShipments, fetchPickups, fetchAddresses]);

  return (
    <div data-testid="customer-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-zinc-500 font-body text-sm">Bentornato,</p>
          <p className="text-xl font-bold text-[#09090B]">
            {user?.name} {user?.company && `- ${user.company}`}
          </p>
          {user?.client_code && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs font-bold tracking-widest uppercase text-zinc-400 font-heading">Codice Cliente:</span>
              <span className="text-sm font-bold tracking-widest text-[#FF5200] font-heading" data-testid="client-code-display">{user.client_code}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(user.client_code); toast.success("Codice copiato!"); }}
                className="p-1 text-zinc-400 hover:text-[#FF5200] transition-colors"
                data-testid="copy-client-code-btn"
                title="Copia codice"
              >
                <Copy size={14} />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <PWAInstallButton scope="customer" variant="compact" />
          <button
            onClick={onLogout}
            className="inline-flex items-center gap-2 border-2 border-zinc-200 px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wide hover:border-zinc-400 transition-colors duration-200 font-heading"
            data-testid="dashboard-logout-btn"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b-2 border-zinc-200 mb-8 overflow-x-auto" data-testid="dashboard-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wide font-heading border-b-2 -mb-[2px] transition-colors duration-200 whitespace-nowrap ${
              activeTab === tab.id
                ? "border-[#FF5200] text-[#FF5200]"
                : "border-transparent text-zinc-400 hover:text-zinc-700"
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "shipments" && (
        <CustomerShipments items={shipments} token={token} mode="spedizione" user={user} onRefresh={fetchShipments} />
      )}
      {activeTab === "new-shipment" && (
        <NewShipmentForm
          token={token}
          addresses={addresses}
          mode="spedizione"
          onCreated={() => { fetchShipments(); setActiveTab("shipments"); }}
        />
      )}
      {activeTab === "pickups" && (
        <CustomerShipments items={pickups} token={token} mode="ritiro" user={user} onRefresh={fetchPickups} />
      )}
      {activeTab === "new-pickup" && (
        <NewShipmentForm
          token={token}
          addresses={addresses}
          mode="ritiro"
          onCreated={() => { fetchPickups(); setActiveTab("pickups"); }}
        />
      )}
      {activeTab === "addresses" && (
        <AddressBook
          addresses={addresses}
          token={token}
          onUpdate={fetchAddresses}
        />
      )}
      {activeTab === "warehouse" && <CustomerWarehouse />}
      {activeTab === "settings" && (
        <ChangePasswordForm token={token} />
      )}
    </div>
  );
}

function ChangePasswordForm({ token }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) { toast.error("Compila tutti i campi"); return; }
    if (newPw.length < 6) { toast.error("La nuova password deve avere almeno 6 caratteri"); return; }
    if (newPw !== confirmPw) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPw, new_password: newPw }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password aggiornata con successo!");
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel cambio password");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full h-11 px-3 border-2 border-zinc-200 rounded-sm focus:border-[#FF5200] outline-none text-sm font-body";
  const lblCls = "block text-xs font-bold tracking-widest uppercase text-zinc-500 mb-1 font-heading";

  return (
    <div className="max-w-md">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-[#09090B] rounded-sm">
          <Lock size={18} className="text-[#FF5200]" />
        </div>
        <div>
          <h3 className="text-lg font-black tracking-tight text-[#09090B] font-heading" data-testid="change-password-title">CAMBIA PASSWORD</h3>
          <p className="text-xs text-zinc-500">Minimo 6 caratteri</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
        <div>
          <label className={lblCls}>Password attuale *</label>
          <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
            className={inputCls} placeholder="Password attuale" data-testid="current-password-input" />
        </div>
        <div>
          <label className={lblCls}>Nuova password *</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            className={inputCls} placeholder="Nuova password (min 6 caratteri)" data-testid="new-password-input" />
        </div>
        <div>
          <label className={lblCls}>Conferma nuova password *</label>
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
            className={inputCls} placeholder="Ripeti nuova password" data-testid="confirm-password-input" />
        </div>
        <button type="submit" disabled={loading}
          className="inline-flex items-center gap-2 bg-[#FF5200] text-white px-6 py-3 font-bold uppercase tracking-wide rounded-sm hover:bg-[#e64a00] transition-colors text-sm font-heading disabled:opacity-50"
          data-testid="change-password-submit-btn">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
          Aggiorna Password
        </button>
      </form>
    </div>
  );
}
